import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, message, Tag, Space } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import supplyChainService, { LogisticsProvider } from '../../../services/supplyChainService';

const LogisticsProviders: React.FC = () => {
    const [providers, setProviders] = useState<LogisticsProvider[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingProvider, setEditingProvider] = useState<LogisticsProvider | null>(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchProviders();
    }, []);

    const fetchProviders = async () => {
        setLoading(true);
        try {
            const res = await supplyChainService.getLogisticsProviders();
            setProviders(res.data);
        } catch (error) {
            message.error('无法加载物流商列表');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (editingProvider) {
                await supplyChainService.updateLogisticsProvider(editingProvider.id!, values);
                message.success('更新成功');
            } else {
                await supplyChainService.createLogisticsProvider(values);
                message.success('创建成功');
            }
            setIsModalVisible(false);
            fetchProviders();
        } catch (error) {

        }
    };

    const columns = [
        { title: '名称', dataIndex: 'name', key: 'name' },
        { title: '代码', dataIndex: 'code', key: 'code' },
        {
            title: '类型', dataIndex: 'provider_type', key: 'provider_type',
            render: (text: string) => {
                const map = { freight_forwarder: '货代', courier: '快递', truck: '卡车' };
                return map[text as keyof typeof map] || text;
            }
        },
        { title: '联系人', dataIndex: 'contact_name', key: 'contact_name' },
        { title: '电话', dataIndex: 'contact_phone', key: 'contact_phone' },
        {
            title: '状态', dataIndex: 'status', key: 'status',
            render: (text: string) => <Tag color={text === 'active' ? 'green' : 'red'}>{text === 'active' ? '启用' : '停用'}</Tag>
        },
        {
            title: '操作', key: 'action',
            render: (_: any, record: LogisticsProvider) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => { setEditingProvider(record); form.setFieldsValue(record); setIsModalVisible(true); }} type="link" />
                    <Button icon={<DeleteOutlined />} danger type="link" onClick={() => handleDelete(record.id!)} />
                </Space>
            )
        }
    ];

    const handleDelete = async (id: number) => {
        try {
            await supplyChainService.deleteLogisticsProvider(id);
            message.success('删除成功');
            fetchProviders();
        } catch (error) {
            message.error('删除失败');
        }
    };

    return (
        <Card title="物流服务商管理" extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingProvider(null); form.resetFields(); setIsModalVisible(true); }}>新增物流商</Button>}>
            <Table dataSource={providers} columns={columns} rowKey="id" loading={loading} />

            <Modal title={editingProvider ? "编辑物流商" : "新增物流商"} open={isModalVisible} onOk={handleSubmit} onCancel={() => setIsModalVisible(false)} destroyOnClose>
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="名称" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="code" label="代码">
                        <Input />
                    </Form.Item>
                    <Form.Item name="provider_type" label="类型" initialValue="freight_forwarder">
                        <Select>
                            <Select.Option value="freight_forwarder">货代</Select.Option>
                            <Select.Option value="courier">快递 (UPS/FedEx)</Select.Option>
                            <Select.Option value="truck">卡车</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="contact_name" label="联系人">
                        <Input />
                    </Form.Item>
                    <Form.Item name="contact_phone" label="电话">
                        <Input />
                    </Form.Item>
                    <Form.Item name="status" label="状态" initialValue="active">
                        <Select>
                            <Select.Option value="active">启用</Select.Option>
                            <Select.Option value="inactive">停用</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default LogisticsProviders;
